import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import ProductTabs from "../components/catalog/ProductTabs";
import { Phone, Check } from "lucide-react";
import DOMPurify from 'dompurify';
import { supabase } from '../lib/supabase';
import { formatPrice } from '../lib/formatPrice';

interface ProductDetail {
  _id: string;
  Title: string;
  Description: string;
  FullInfo?: string;
  Price?: string;
  OldPrice?: string;
  WarrantyPeriod?: string;
  Images: string[];
  Category?: string;
  StockCode?: string;
  Brand?: string;
  MinMotorPower?: string;
  MaxMotorPower?: string;
  DefaultEquipments?: string[];
  ExtraEquipments?: (string | { name: string; price: number })[];
  Videos?: string[];
}

interface ProductPageProps {
  type?: "boat" | "used" | "engine" | "product";
}

const StandardProductDetailPage: React.FC<ProductPageProps> = ({ type }) => {
  const { slug } = useParams();
  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedMedia, setSelectedMedia] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchProduct = async () => {
      const path = window.location.pathname;
      const table = type === "product" || path.includes("/aksesuar/") ? "products" : "engines";

      setLoading(true);
      const { data, error } = await supabase.from(table).select('*').eq('id', slug).single();

      if (error || !data) {
        console.error("Veri alınamadı", error);
        setLoading(false);
        return;
      }

      const normalized: ProductDetail = {
        _id: data.id,
        Title: data.Title || data.title || data.name,
        Description: data.Description || data.description,
        FullInfo: data.FullInfo || data.fullInfo || data.description,
        Price: data.Price || data.price,
        OldPrice: data.OldPrice || data.oldPrice,
        WarrantyPeriod: data.WarrantyPeriod || data.warrantyPeriod,
        Images: (data.Images && data.Images.length > 0) ? data.Images : (data.imageUrl ? [data.imageUrl] : []),
        Brand: data.brand || data.Brand,
        MinMotorPower: data.MinMotorPower || data.minMotorPower,
        MaxMotorPower: data.MaxMotorPower || data.maxMotorPower,
        DefaultEquipments: data.DefaultEquipments || data.defaultEquipments,
        ExtraEquipments: data.ExtraEquipments || data.extraEquipments,
        Videos: data.Videos || data.videos || [],
        Category: data.Category || data.category || (type === "engine" ? "Dıştan Takma Motorlar" : "Aksesuar"),
        StockCode: data.StockCode || data.stockCode,
      };

      setProduct(normalized);
      if (normalized.Images.length > 0) setSelectedMedia(normalized.Images[0]);
      setLoading(false);
    };

    fetchProduct();
  }, [slug, type]);

  if (loading) return (
    <div className="flex justify-center items-center min-h-screen bg-white">
      <div className="w-12 h-12 border-4 border-[#002744] border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  if (!product) return <div className="p-20 text-center text-xl font-light">Ürün bulunamadı.</div>;

  const tabs = [
    {
      id: 'info',
      label: 'Ürün Bilgisi',
      content: (
        <div className="prose prose-sm max-w-none text-gray-600 leading-relaxed">
          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.Description) }} />
          {product.FullInfo && product.FullInfo !== product.Description && (
            <div className="mt-8" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.FullInfo) }} />
          )}
        </div>
      )
    },
    ...((product.MinMotorPower || product.MaxMotorPower) ? [{
      id: 'specs',
      label: 'Teknik Özellikler',
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4 text-sm">
          {product.MinMotorPower && (
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="font-medium text-gray-900">Min. Güç</span>
              <span className="text-gray-600">{product.MinMotorPower} HP</span>
            </div>
          )}
          {product.MaxMotorPower && (
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="font-medium text-gray-900">Maks. Güç</span>
              <span className="text-gray-600">{product.MaxMotorPower} HP</span>
            </div>
          )}
        </div>
      )
    }] : []),
    ...(product.Videos && product.Videos.length > 0 ? [{
      id: 'video',
      label: 'Videolar',
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {product.Videos!.map((vid, idx) => (
            <video key={idx} src={vid} controls className="w-full rounded-lg shadow-sm" />
          ))}
        </div>
      )
    }] : []),
    ...(product.DefaultEquipments && product.DefaultEquipments.length > 0 ? [{
      id: 'equipments',
      label: 'Donanım',
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {product.DefaultEquipments!.map((item, idx) => (
            <div key={idx} className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5 shrink-0">
                <Check size={12} className="text-green-600" />
              </div>
              <span className="text-gray-700 text-sm">{item}</span>
            </div>
          ))}
        </div>
      )
    }] : []),
  ];

  return (
    <div className="bg-white min-h-screen font-montserrat text-gray-800">
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">

          {/* LEFT: Gallery */}
          <div className="lg:col-span-5">
            <div className="flex flex-col-reverse md:flex-row gap-4 h-[500px]">
              {product.Images.length > 1 && (
                <div className="flex md:flex-col gap-3 overflow-auto md:w-20 shrink-0">
                  {product.Images.map((img, idx) => (
                    <div
                      key={idx}
                      onClick={() => setSelectedMedia(img)}
                      className={`w-16 h-16 md:w-20 md:h-20 border rounded cursor-pointer overflow-hidden transition-all ${selectedMedia === img ? 'border-blue-600 ring-1 ring-blue-600 opacity-100' : 'border-gray-200 opacity-70 hover:opacity-100'}`}
                    >
                      <img src={img} className="w-full h-full object-contain bg-white" alt={`Görsel ${idx + 1}`} />
                    </div>
                  ))}
                </div>
              )}

              <div className="flex-1 border border-gray-200 rounded-lg p-4 flex items-center justify-center bg-white">
                {product.Images.length > 0 ? (
                  <img
                    src={selectedMedia || product.Images[0]}
                    className="max-w-full max-h-full object-contain"
                    alt={product.Title}
                  />
                ) : (
                  <div className="text-gray-400 text-sm">Görsel yok</div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT: Info */}
          <div className="lg:col-span-7 space-y-5">

            {product.Brand && (
              <div className="text-sm text-gray-400 font-medium uppercase tracking-widest">{product.Brand}</div>
            )}

            <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">{product.Title}</h1>

            {/* Meta */}
            <div className="text-sm space-y-1.5 text-gray-600 border-t border-b border-gray-100 py-4">
              {product.Category && (
                <div className="flex">
                  <span className="w-36 font-medium text-gray-700">Kategori</span>
                  <span>: {product.Category}</span>
                </div>
              )}
              {product.StockCode && (
                <div className="flex">
                  <span className="w-36 font-medium text-gray-700">Stok Kodu</span>
                  <span>: {product.StockCode}</span>
                </div>
              )}
              {product.WarrantyPeriod && (
                <div className="flex">
                  <span className="w-36 font-medium text-gray-700">Garanti Süresi</span>
                  <span>: {product.WarrantyPeriod}</span>
                </div>
              )}
            </div>

            {/* Price */}
            {product.Price && (
              <div className="py-2">
                {product.OldPrice && (
                  <div className="text-gray-400 line-through text-lg mb-1">{formatPrice(product.OldPrice)} ₺</div>
                )}
                <div className="text-4xl font-bold text-gray-900 tracking-tight">
                  {formatPrice(product.Price)} <span className="text-xl font-normal text-gray-500">₺</span>
                </div>
                <div className="text-xs text-gray-400 mt-1">KDV Hariç Fiyattır</div>
              </div>
            )}

            {/* Contact Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <a
                href="https://wa.me/905326036152"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-green-500 hover:bg-green-600 text-white font-medium h-12 rounded-lg flex items-center justify-center gap-2 transition-colors"
              >
                <Phone size={18} />
                Whatsapp
              </a>
              <Link
                to="/iletisim"
                className="flex-1 bg-[#002744] hover:bg-[#003a6b] text-white font-medium h-12 rounded-lg flex items-center justify-center gap-2 transition-colors"
              >
                İletişime Geç
              </Link>
            </div>

          </div>
        </div>

        {/* Tabs */}
        <div className="mt-16 md:mt-20">
          <ProductTabs tabs={tabs} />
        </div>
      </div>
    </div>
  );
};

export default StandardProductDetailPage;
